import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { openPrintUrl } from '../../../../lib/openPrint';
import {
  CATEGORICAL_PALETTE,
  CHART_COLORS,
  buildPrintReportUrl,
  downloadCsv,
  formatDateRangeLabel,
  studentNameLookup,
} from '../../reportUtils';

// ---------------------------------------------------------------------------
// IEP Progress Report
//
// Answers "how are my kids doing on their goals right now?" from real data.
// Every number here is computed on the fly from the two loaded contexts;
// no server-side aggregation, no stale cached table. Filters apply as
// follows:
//
//   * `studentFilter` (Set<id> | null) narrows every rollup to just those
//     students. null / empty === all active students.
//   * Date range is intentionally NOT applied — this is a snapshot report:
//     "what's the current status of goals?" not "what happened in Sept".
//     The Data Collection and Behavior Trends reports honor date range.
// ---------------------------------------------------------------------------

const STATUS_LABELS = {
  in_progress: 'In progress',
  mastered: 'Mastered',
  discontinued: 'Discontinued',
  on_hold: 'On hold',
  not_started: 'Not started',
};

const STATUS_COLORS = {
  in_progress: CHART_COLORS.primary,
  mastered: CHART_COLORS.success,
  discontinued: CHART_COLORS.slate,
  on_hold: CHART_COLORS.warning,
  not_started: CHART_COLORS.danger,
};

const IepProgressReport = ({ dateRange, studentFilter, printMode = false }) => {
  const { students } = useStudents();
  const { goals, loading, error } = useIEPGoals();
  // Recharts animates by default — great on screen, terrible for print.
  // The initial frame renders as an empty axis and the animation kicks
  // in ~600ms later, which is too late for browsers that snapshot the
  // page before firing window.print(). Disabling animation for print
  // ensures the first paint IS the final paint.
  const animate = !printMode;
  const handlePrint = () => {
    const url = buildPrintReportUrl('iep-progress', { dateRange, studentFilter });
    openPrintUrl(url);
  };

  const rollups = useMemo(() => {
    const activeStudents = (Array.isArray(students) ? students : []).filter(
      (s) => (s.status || 'active') !== 'archived',
    );
    const filterActive = studentFilter && studentFilter.size > 0;
    const inScopeStudents = filterActive
      ? activeStudents.filter((s) => studentFilter.has(s.id))
      : activeStudents;
    const inScopeStudentIds = new Set(inScopeStudents.map((s) => s.id));

    const scopedGoals = (Array.isArray(goals) ? goals : []).filter((g) =>
      inScopeStudentIds.has(g.studentId),
    );

    const nameFor = studentNameLookup(students);

    // KPI numbers
    const totalGoals = scopedGoals.length;
    const inProgress = scopedGoals.filter(
      (g) => (g.status || 'in_progress') === 'in_progress',
    ).length;
    const mastered = scopedGoals.filter((g) => g.status === 'mastered').length;
    const progressPercents = scopedGoals
      .map((g) => Number(g.progressPercent))
      .filter((n) => Number.isFinite(n));
    const avgProgress = progressPercents.length
      ? Math.round(
          progressPercents.reduce((a, b) => a + b, 0) / progressPercents.length,
        )
      : 0;

    // Status distribution (pie chart)
    const statusCounts = new Map();
    for (const g of scopedGoals) {
      const key = g.status || 'in_progress';
      statusCounts.set(key, (statusCounts.get(key) || 0) + 1);
    }
    const statusDistribution = Array.from(statusCounts.entries()).map(
      ([status, count]) => ({
        name: STATUS_LABELS[status] || status,
        value: count,
        color: STATUS_COLORS[status] || CHART_COLORS.slate,
      }),
    );

    // Per-student rollup
    const perStudent = inScopeStudents
      .map((s) => {
        const gs = scopedGoals.filter((g) => g.studentId === s.id);
        const ps = gs
          .map((g) => Number(g.progressPercent))
          .filter((n) => Number.isFinite(n));
        const avg = ps.length
          ? Math.round(ps.reduce((a, b) => a + b, 0) / ps.length)
          : 0;
        return {
          id: s.id,
          name: nameFor.get(s.id) || 'Unknown',
          goals: gs.length,
          mastered: gs.filter((g) => g.status === 'mastered').length,
          inProgress: gs.filter(
            (g) => (g.status || 'in_progress') === 'in_progress',
          ).length,
          avgProgress: avg,
        };
      })
      .filter((r) => r.goals > 0)
      .sort((a, b) => b.avgProgress - a.avgProgress);

    // By goal area (pie)
    const areaCounts = new Map();
    for (const g of scopedGoals) {
      const key = (g.goalArea || 'Unspecified').trim() || 'Unspecified';
      areaCounts.set(key, (areaCounts.get(key) || 0) + 1);
    }
    const byArea = Array.from(areaCounts.entries())
      .map(([area, count], idx) => ({
        name: area,
        value: count,
        color: CATEGORICAL_PALETTE[idx % CATEGORICAL_PALETTE.length],
      }))
      .sort((a, b) => b.value - a.value);

    return {
      totalGoals,
      inProgress,
      mastered,
      avgProgress,
      statusDistribution,
      perStudent,
      byArea,
      inScopeStudents,
    };
  }, [students, goals, studentFilter]);

  const handleExportCsv = () => {
    const filename = `iep-progress-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(
      filename,
      rollups.perStudent,
      [
        { key: 'name', label: 'Student' },
        { key: 'goals', label: 'Total goals' },
        { key: 'inProgress', label: 'In progress' },
        { key: 'mastered', label: 'Mastered' },
        { key: 'avgProgress', label: 'Avg progress %' },
      ],
    );
  };

  if (error) {
    return (
      <div className="p-4 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-foreground">
        <p className="font-medium">Couldn't load goals</p>
        <p className="text-muted-foreground mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="Users"
          label="Students in report"
          value={String(rollups.inScopeStudents.length)}
          tone="primary"
        />
        <KpiCard
          icon="Target"
          label="Total goals"
          value={String(rollups.totalGoals)}
          tone="primary"
          subtitle={`${rollups.inProgress} in progress`}
        />
        <KpiCard
          icon="CheckCircle2"
          label="Mastered"
          value={String(rollups.mastered)}
          tone="success"
          subtitle={
            rollups.totalGoals > 0
              ? `${Math.round(
                  (rollups.mastered / rollups.totalGoals) * 100,
                )}% of goals`
              : undefined
          }
        />
        <KpiCard
          icon="BarChart3"
          label="Avg. progress"
          value={`${rollups.avgProgress}%`}
          tone="primary"
          subtitle="Across all goals in scope"
        />
      </div>

      {/* Header + export */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            IEP Progress – Snapshot
          </h2>
          <p className="text-sm text-muted-foreground">
            Current status of every goal for {rollups.inScopeStudents.length} student
            {rollups.inScopeStudents.length === 1 ? '' : 's'}.
            {' '}
            <span className="text-xs">
              (Filters: {formatDateRangeLabel(dateRange)}. Date range is
              informational only for this report — goal status reflects
              current values.)
            </span>
          </p>
        </div>
        {!printMode && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
              title={
                rollups.totalGoals === 0
                  ? 'Opens the print view — will show "no goals in this filter yet".'
                  : 'Open the print-ready view for this report'
              }
            >
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              iconName="Download"
              iconPosition="left"
              disabled={rollups.perStudent.length === 0}
            >
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {loading && rollups.totalGoals === 0 && (
        <div className="p-4 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground text-center">
          Loading goals…
        </div>
      )}

      {rollups.totalGoals === 0 && !loading ? (
        <EmptyState
          icon="Target"
          title="No goals yet"
          body="Once you add IEP goals to your students, this report will fill itself in automatically. No mock data — what you see is what you have."
        />
      ) : (
        <>
          {/* Two charts side by side on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Goals by status">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={rollups.statusDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    label={({ name, value }) => `${name}: ${value}`}
                    isAnimationActive={animate}
                  >
                    {rollups.statusDistribution.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  {!printMode && <Tooltip />}
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Goals by area">
              {rollups.byArea.length === 0 ? (
                <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
                  No goal areas recorded
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={rollups.byArea}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value }) => `${name}: ${value}`}
                      isAnimationActive={animate}
                    >
                      {rollups.byArea.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    {!printMode && <Tooltip />}
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <ChartCard title="Average progress % by student">
            <ResponsiveContainer
              width="100%"
              height={Math.max(200, rollups.perStudent.length * 30)}
            >
              <BarChart
                data={rollups.perStudent}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  stroke="#6B7280"
                  fontSize={12}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#6B7280"
                  width={140}
                  fontSize={12}
                />
                {!printMode && (
                  <Tooltip
                    formatter={(v) => [`${v}%`, 'Avg progress']}
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                )}
                <Bar
                  dataKey="avgProgress"
                  fill={CHART_COLORS.primary}
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={animate}
                  label={printMode ? { position: 'right', fontSize: 11, fill: '#111827', formatter: (v) => `${v}%` } : undefined}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Data table */}
          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-medium text-foreground">
                Per-student summary
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3">Student</th>
                    <th className="p-3">Goals</th>
                    <th className="p-3">In progress</th>
                    <th className="p-3">Mastered</th>
                    <th className="p-3">Avg progress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rollups.perStudent.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="p-3 font-medium text-foreground">
                        {r.name}
                      </td>
                      <td className="p-3 text-foreground">{r.goals}</td>
                      <td className="p-3 text-foreground">{r.inProgress}</td>
                      <td className="p-3 text-foreground">{r.mastered}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 max-w-[120px] h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${r.avgProgress}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-10 text-right">
                            {r.avgProgress}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// -- Local presentational helpers ----------------------------------------

const TONE_STYLES = {
  primary: {
    icon: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  success: {
    icon: 'text-green-600',
    bg: 'bg-green-50',
  },
  warning: {
    icon: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  danger: {
    icon: 'text-red-600',
    bg: 'bg-red-50',
  },
};

export const KpiCard = ({ icon, label, value, tone = 'primary', subtitle }) => {
  const styles = TONE_STYLES[tone] || TONE_STYLES.primary;
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className={`w-9 h-9 ${styles.bg} rounded-lg flex items-center justify-center mb-3`}>
        <Icon name={icon} size={18} className={styles.icon} />
      </div>
      <p className="text-2xl font-bold text-foreground leading-tight">
        {value}
      </p>
      <p className="text-sm text-foreground mt-0.5">{label}</p>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  );
};

export const ChartCard = ({ title, children }) => (
  <div className="bg-surface border border-border rounded-lg p-4 print:break-inside-avoid">
    <h3 className="font-medium text-foreground mb-3">{title}</h3>
    {children}
  </div>
);

export const EmptyState = ({ icon, title, body }) => (
  <div className="bg-surface border border-dashed border-border rounded-lg p-8 text-center">
    <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center mb-3">
      <Icon name={icon} size={22} className="text-muted-foreground" />
    </div>
    <h3 className="font-medium text-foreground mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground max-w-md mx-auto">{body}</p>
  </div>
);

export default IepProgressReport;
